import React, { useState } from 'react';
import { Calendar, User, ArrowRight, Search, Heart, Eye, MessageCircle, X, ChevronLeft, ChevronRight } from 'lucide-react';

const Blog = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [likedPosts, setLikedPosts] = useState({});
  const [expandedPost, setExpandedPost] = useState(null);

  const blogPosts = [
    {
      id: 1,
      title: 'Top 10 Event Trends for 2025',
      excerpt: 'Discover the latest trends shaping the event management industry in 2025, from hybrid events to sustainable practices.',
      fullContent: `
        <h3>Introduction</h3>
        <p>The event management industry is evolving rapidly, and 2025 promises to be a year of innovation and transformation. From technological advancements to sustainability initiatives, here are the top 10 trends that will shape the future of events.</p>
        
        <h3>1. Hybrid Events Become the Standard</h3>
        <p>Hybrid events combining in-person and virtual experiences are no longer just a trend—they're becoming the standard. This format allows organizers to reach global audiences while maintaining the intimacy of face-to-face interactions.</p>
        
        <h3>2. Sustainability Takes Center Stage</h3>
        <p>Eco-friendly practices are now essential. From zero-waste catering to carbon-neutral venues, sustainability is a key consideration for modern event planners and attendees alike.</p>
        
        <h3>3. AI-Powered Personalization</h3>
        <p>Artificial intelligence is revolutionizing event experiences by providing personalized recommendations, matchmaking attendees, and optimizing event schedules based on individual preferences.</p>
        
        <h3>4. Immersive Technology Experiences</h3>
        <p>Virtual reality, augmented reality, and mixed reality are creating unforgettable immersive experiences that engage attendees in ways never before possible.</p>
        
        <h3>5. Wellness-Focused Events</h3>
        <p>Mental health and wellness are becoming integral parts of event programming, with meditation sessions, wellness breaks, and healthy catering options becoming standard.</p>
        
        <h3>Conclusion</h3>
        <p>As we move into 2025, these trends will continue to evolve and shape how we create memorable event experiences. Stay ahead by embracing innovation while keeping your attendees' needs at the forefront.</p>
      `,
      author: 'Rashmi Sharma',
      date: 'Dec 5, 2024',
      category: 'Trends',
      image: '/blog/blog1.jpeg',
      readTime: '5 min read',
      views: 1245,
      comments: 23,
      likes: 89
    },
    {
      id: 2,
      title: 'How to Plan a Successful Corporate Event',
      excerpt: 'A comprehensive guide to organizing memorable corporate events that engage employees and leave lasting impressions.',
      fullContent: `
        <h3>Planning Your Corporate Event</h3>
        <p>Corporate events are powerful tools for building team spirit, celebrating achievements, and fostering professional relationships. Here's your complete guide to planning an unforgettable corporate event.</p>
        
        <h3>Step 1: Define Your Objectives</h3>
        <p>Before diving into logistics, clearly define what you want to achieve. Is it team building, product launch, or celebrating milestones? Your objectives will guide every decision.</p>
        
        <h3>Step 2: Budget Planning</h3>
        <p>Create a realistic budget that covers venue, catering, entertainment, technology, and contingencies. Remember to allocate 10-15% for unexpected expenses.</p>
        
        <h3>Step 3: Choose the Right Venue</h3>
        <p>Your venue sets the tone for the entire event. Consider location, capacity, amenities, and ambiance when making your selection.</p>
        
        <h3>Step 4: Create Engaging Content</h3>
        <p>Design a program that balances information with entertainment. Include interactive sessions, networking opportunities, and memorable moments.</p>
        
        <h3>Key Takeaways</h3>
        <p>Success lies in meticulous planning, clear communication, and attention to detail. With these strategies, your corporate event will be both professional and memorable.</p>
      `,
      author: 'Prawal Kumar',
      date: 'Nov 28, 2024',
      category: 'Corporate',
      image: '/blog/blog2.jpeg',
      readTime: '8 min read',
      views: 2130,
      comments: 45,
      likes: 156
    },
    {
      id: 3,
      title: 'Wedding Planning 101: Essential Checklist',
      excerpt: 'Everything you need to know about planning your dream wedding, from venue selection to final touches.',
      fullContent: `
        <h3>Your Dream Wedding Starts Here</h3>
        <p>Planning a wedding can be overwhelming, but with the right approach and checklist, you can create the perfect day you've always dreamed of.</p>
        
        <h3>12 Months Before</h3>
        <p>Set your budget, create guest list, book venue, and hire key vendors like photographer and caterer. This is the foundation of your wedding planning.</p>
        
        <h3>6 Months Before</h3>
        <p>Send save-the-dates, shop for wedding attire, finalize menu, and book entertainment. Details start coming together at this stage.</p>
        
        <h3>3 Months Before</h3>
        <p>Send formal invitations, finalize decor, arrange transportation, and have final vendor meetings. The excitement builds!</p>
        
        <h3>1 Month Before</h3>
        <p>Confirm all details, create seating chart, prepare ceremony and reception timeline, and enjoy your last weeks before the big day!</p>
        
        <h3>Final Thoughts</h3>
        <p>Remember, your wedding day is about celebrating love. Don't sweat the small stuff—focus on what truly matters: your partnership and the people you love.</p>
      `,
      author: 'Ananya Reddy',
      date: 'Nov 20, 2024',
      category: 'Weddings',
      image: '/blog/blog3.jpeg',
      readTime: '10 min read',
      views: 3420,
      comments: 78,
      likes: 234
    },
    {
      id: 4,
      title: 'The Rise of Virtual and Hybrid Events',
      excerpt: 'Explore how virtual and hybrid events are transforming the industry and creating new opportunities for engagement.',
      fullContent: `
        <h3>The Digital Revolution in Events</h3>
        <p>Virtual and hybrid events have transformed from emergency solutions to strategic choices, offering unprecedented reach and engagement opportunities.</p>
        
        <h3>Benefits of Virtual Events</h3>
        <p>Global reach, cost savings, increased accessibility, detailed analytics, and environmental sustainability make virtual events attractive to organizers and attendees alike.</p>
        
        <h3>Hybrid Event Best Practices</h3>
        <p>Create parallel experiences, ensure equal engagement for both audiences, invest in quality technology, and design interactive elements that work for everyone.</p>
        
        <h3>Technology Platforms</h3>
        <p>Choose platforms that offer networking features, interactive tools, reliable streaming, and robust analytics to measure success.</p>
        
        <h3>Looking Forward</h3>
        <p>The future is hybrid. By embracing both physical and digital experiences, event organizers can create more inclusive, engaging, and impactful events.</p>
      `,
      author: 'Manoj Singh',
      date: 'Nov 15, 2024',
      category: 'Technology',
      image: '/blog/blog4.jpeg',
      readTime: '6 min read',
      views: 1890,
      comments: 34,
      likes: 112
    },
    {
      id: 5,
      title: 'Budget-Friendly Event Decoration Ideas',
      excerpt: 'Creative and cost-effective decoration ideas that will make your event stunning without breaking the bank.',
      fullContent: `
        <h3>Beautiful Events on a Budget</h3>
        <p>You don't need a massive budget to create stunning event decor. With creativity and smart planning, you can achieve impressive results while staying within budget.</p>
        
        <h3>DIY Centerpieces</h3>
        <p>Create beautiful centerpieces using seasonal flowers, candles, and repurposed materials. Mason jars, wine bottles, and thrifted vases can all become elegant decor elements.</p>
        
        <h3>Lighting Magic</h3>
        <p>String lights, uplighting, and candles create ambiance without high costs. Strategic lighting can transform any space dramatically.</p>
        
        <h3>Natural Elements</h3>
        <p>Incorporate greenery, branches, stones, and other natural materials. These are often free or inexpensive and add organic beauty to your decor.</p>
        
        <h3>Smart Shopping</h3>
        <p>Shop off-season, rent instead of buy, and look for second-hand decorations. Many items can be purchased at a fraction of retail cost.</p>
        
        <h3>Final Tips</h3>
        <p>Focus on a few statement pieces rather than decorating everything. Quality over quantity creates more impact while saving money.</p>
      `,
      author: 'Sonia Malhotra',
      date: 'Nov 10, 2024',
      category: 'Decoration',
      image: '/blog/blog5.jpeg',
      readTime: '7 min read',
      views: 2567,
      comments: 56,
      likes: 178
    },
    {
      id: 6,
      title: 'Corporate Team Building Activities That Work',
      excerpt: 'Proven team building activities that boost morale, improve communication, and strengthen workplace relationships.',
      fullContent: `
        <h3>Building Stronger Teams</h3>
        <p>Effective team building goes beyond trust falls and icebreakers. These proven activities create lasting bonds and improve workplace dynamics.</p>
        
        <h3>Escape Room Challenges</h3>
        <p>Escape rooms require collaboration, communication, and creative problem-solving—essential workplace skills developed in a fun, engaging environment.</p>
        
        <h3>Outdoor Adventures</h3>
        <p>Hiking, obstacle courses, and adventure activities push teams out of their comfort zones and build trust through shared challenges.</p>
        
        <h3>Creative Workshops</h3>
        <p>Art classes, cooking sessions, and improv theater encourage creativity and collaboration while providing a break from routine work environments.</p>
        
        <h3>Volunteer Together</h3>
        <p>Community service projects build team spirit while making a positive impact. Shared purpose strengthens bonds and builds pride.</p>
        
        <h3>Measuring Success</h3>
        <p>The best team building activities translate to improved workplace communication, increased collaboration, and stronger professional relationships that last beyond the event.</p>
      `,
      author: 'Vikram Patel',
      date: 'Nov 5, 2024',
      category: 'Corporate',
      image: '/blog/blog6.jpeg',
      readTime: '6 min read',
      views: 1678,
      comments: 29,
      likes: 95
    }
  ];

  const categories = ['All', 'Trends', 'Corporate', 'Weddings', 'Technology', 'Decoration'];
  const [selectedCategory, setSelectedCategory] = useState('All');

  const filteredPosts = blogPosts.filter(post => {
    const matchesCategory = selectedCategory === 'All' || post.category === selectedCategory;
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const toggleLike = (postId, event) => {
    event.stopPropagation();
    setLikedPosts(prev => ({
      ...prev,
      [postId]: !prev[postId]
    }));
  };

  const getLikeCount = (postId, baseLikes) => {
    return likedPosts[postId] ? baseLikes + 1 : baseLikes;
  };

  const openPost = (post) => {
    setExpandedPost(post);
    document.body.style.overflow = 'hidden'; // Prevent background scroll
  };

  const closePost = () => {
    setExpandedPost(null);
    document.body.style.overflow = 'auto'; // Restore scroll
  };

  return (
    <section id="blog" className="py-20 md:py-28 bg-gradient-to-br from-orange-50/30 via-white to-[#b8d4d4]/20 relative overflow-hidden">
      {/* Background Decorative Elements */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-to-br from-orange-200/30 to-transparent rounded-full blur-3xl -z-10"></div>
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-gradient-to-tr from-[#b8d4d4]/30 to-transparent rounded-full blur-3xl -z-10"></div>

      <div className="container mx-auto px-6">
        {/* Header Section */}
        <div className="text-center max-w-4xl mx-auto mb-16">
          <p className="text-orange-500 uppercase tracking-widest text-sm font-bold mb-3" style={{ fontFamily: "'Hepta Slab', serif" }}>
            Our Blog
          </p>
          <h2 className="text-6xl md:text-7xl font-black text-gray-900 mb-6 leading-tight" style={{ fontFamily: "'Diplomata', cursive", letterSpacing: '-0.02em' }}>
            Latest Stories
          </h2>
          <p className="text-xl text-gray-600 leading-relaxed font-medium" style={{ fontFamily: "'Hepta Slab', serif" }}>
            Insights, tips, and inspiration for creating unforgettable events
          </p>
        </div>

        {/* Search Bar */}
        <div className="max-w-2xl mx-auto mb-10">
          <div className="relative">
            <Search className="absolute left-5 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search for articles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-14 pr-6 py-4 rounded-2xl border border-gray-200 focus:border-orange-400 focus:outline-none focus:ring-4 focus:ring-orange-100 transition-all shadow-sm"
              style={{ fontFamily: "'Hepta Slab', serif" }}
            />
          </div>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-16">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-7 py-3 rounded-full font-bold text-sm transition-all duration-300 ${
                selectedCategory === category
                  ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg shadow-orange-200 scale-105'
                  : 'bg-white text-gray-700 border border-gray-200 hover:border-orange-400 hover:shadow-md'
              }`}
              style={{ fontFamily: "'Hepta Slab', serif" }}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Mobile: Horizontal Scroll Cards */}
        <div className="block md:hidden mb-10">
          <div className="flex overflow-x-auto gap-6 pb-6 snap-x snap-mandatory scrollbar-hide -mx-6 px-6">
            {filteredPosts.map((post) => (
              <article
                key={post.id}
                className="flex-shrink-0 w-[85vw] bg-white rounded-3xl shadow-md overflow-hidden snap-center"
              >
                {/* Image */}
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-4 left-4 bg-white/95 backdrop-blur-sm text-orange-600 px-4 py-1.5 rounded-full text-xs font-bold shadow-lg" style={{ fontFamily: "'Hepta Slab', serif" }}>
                    {post.category}
                  </div>
                  <button
                    onClick={(e) => toggleLike(post.id, e)}
                    className="absolute top-4 right-4 p-2.5 bg-white/95 backdrop-blur-sm rounded-full shadow-lg"
                  >
                    <Heart
                      className={`w-4 h-4 transition-all ${
                        likedPosts[post.id]
                          ? 'fill-red-500 text-red-500'
                          : 'text-gray-600'
                      }`}
                    />
                  </button>
                </div>

                {/* Content */}
                <div className="p-5">
                  <div className="flex items-center gap-3 mb-3 text-xs text-gray-500">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      <span style={{ fontFamily: "'Hepta Slab', serif" }}>{post.date}</span>
                    </div>
                    <div className="w-1 h-1 rounded-full bg-gray-300"></div>
                    <span style={{ fontFamily: "'Hepta Slab', serif" }}>{post.readTime}</span>
                  </div>

                  <h3 
                    className="text-lg font-black text-gray-900 mb-2 line-clamp-2 leading-tight" 
                    style={{ fontFamily: "'Diplomata', 'Arbutus', cursive" }}
                  >
                    {post.title}
                  </h3>

                  <p 
                    className="text-gray-600 text-sm mb-4 line-clamp-2 leading-relaxed" 
                    style={{ fontFamily: "'Hepta Slab', serif" }}
                  >
                    {post.excerpt}
                  </p>

                  <button 
                    onClick={() => openPost(post)}
                    className="flex items-center gap-2 text-orange-600 font-bold text-sm"
                  >
                    <span style={{ fontFamily: "'Hepta Slab', serif" }}>Read More</span>
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </article>
            ))}
          </div>
          
          {/* Scroll Indicator */}
          <div className="flex justify-center gap-2 mt-4">
            {filteredPosts.map((_, index) => (
              <div key={index} className="w-2 h-2 rounded-full bg-gray-300"></div>
            ))}
          </div>
        </div>

        {/* Desktop: Grid View */}
        <div className="hidden md:grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 max-w-7xl mx-auto">
          {filteredPosts.map((post) => (
            <article
              key={post.id}
              className="group bg-white rounded-3xl shadow-md hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 overflow-hidden"
            >
              {/* Image */}
              <div className="relative h-64 overflow-hidden">
                <img
                  src={post.image}
                  alt={post.title}
                  className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute top-5 left-5 bg-white/95 backdrop-blur-sm text-orange-600 px-5 py-2 rounded-full text-xs font-bold shadow-lg" style={{ fontFamily: "'Hepta Slab', serif" }}>
                  {post.category}
                </div>
                <button
                  onClick={(e) => toggleLike(post.id, e)}
                  className="absolute top-5 right-5 p-3 bg-white/95 backdrop-blur-sm rounded-full shadow-lg hover:scale-110 transition-transform"
                >
                  <Heart
                    className={`w-5 h-5 transition-all ${
                      likedPosts[post.id]
                        ? 'fill-red-500 text-red-500'
                        : 'text-gray-600'
                    }`}
                  />
                </button>
              </div>

              {/* Content */}
              <div className="p-7">
                <div className="flex items-center gap-4 mb-4 text-sm text-gray-500">
                  <div className="flex items-center gap-1.5">
                    <Calendar className="w-4 h-4" />
                    <span style={{ fontFamily: "'Hepta Slab', serif" }}>{post.date}</span>
                  </div>
                  <div className="w-1 h-1 rounded-full bg-gray-300"></div>
                  <div className="flex items-center gap-1.5">
                    <User className="w-4 h-4" />
                    <span style={{ fontFamily: "'Hepta Slab', serif" }}>{post.author}</span>
                  </div>
                </div>

                <h3 
                  className="text-2xl font-black text-gray-900 mb-3 group-hover:text-orange-600 transition-colors line-clamp-2 leading-tight" 
                  style={{ fontFamily: "'Diplomata', 'Arbutus', cursive" }}
                >
                  {post.title}
                </h3>

                <p 
                  className="text-gray-600 mb-5 line-clamp-3 leading-relaxed" 
                  style={{ fontFamily: "'Hepta Slab', serif" }}
                >
                  {post.excerpt}
                </p>

                <div className="flex items-center justify-between pb-5 mb-5 border-b border-gray-100">
                  <div className="flex items-center gap-5 text-sm text-gray-500">
                    <div className="flex items-center gap-1.5">
                      <Eye className="w-4 h-4" />
                      <span style={{ fontFamily: "'Hepta Slab', serif" }}>{post.views}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <MessageCircle className="w-4 h-4" />
                      <span style={{ fontFamily: "'Hepta Slab', serif" }}>{post.comments}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Heart className={`w-4 h-4 ${likedPosts[post.id] ? 'fill-red-500 text-red-500' : ''}`} />
                      <span style={{ fontFamily: "'Hepta Slab', serif" }}>{getLikeCount(post.id, post.likes)}</span>
                    </div>
                  </div>
                  <span className="text-xs text-gray-400" style={{ fontFamily: "'Hepta Slab', serif" }}>
                    {post.readTime}
                  </span>
                </div>

                <button 
                  onClick={() => openPost(post)}
                  className="flex items-center gap-2 text-orange-600 font-bold hover:gap-4 transition-all duration-300 group/btn"
                >
                  <span style={{ fontFamily: "'Hepta Slab', serif", fontSize: '16px' }}>Read Article</span>
                  <ArrowRight className="w-5 h-5 group-hover/btn:translate-x-1 transition-transform" />
                </button>
              </div>
            </article>
          ))}
        </div>

        {/* No Results Message */}
        {filteredPosts.length === 0 && (
          <div className="text-center py-20">
            <p className="text-2xl text-gray-500 font-medium" style={{ fontFamily: "'Hepta Slab', serif" }}>
              No articles found. Try a different search or category.
            </p>
          </div>
        )}
      </div>

      {/* Full Blog Post Modal */}
      {expandedPost && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 overflow-y-auto">
          <div className="min-h-screen py-8 px-4 flex items-start justify-center">
            <div className="bg-white rounded-3xl max-w-4xl w-full shadow-2xl relative">
              
              {/* Close Button */}
              <button
                onClick={closePost}
                className="absolute top-6 right-6 p-3 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors z-10"
              >
                <X className="w-6 h-6 text-gray-700" />
              </button>

              {/* Header Image */}
              <div className="relative h-96 rounded-t-3xl overflow-hidden">
                <img
                  src={expandedPost.image}
                  alt={expandedPost.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-8 left-8 right-8">
                  <div className="inline-block bg-orange-500 text-white px-4 py-2 rounded-full text-sm font-bold mb-4" style={{ fontFamily: "'Hepta Slab', serif" }}>
                    {expandedPost.category}
                  </div>
                  <h1 
                    className="text-4xl md:text-5xl font-black text-white mb-4 leading-tight" 
                    style={{ fontFamily: "'Diplomata', 'Arbutus', cursive" }}
                  >
                    {expandedPost.title}
                  </h1>
                  <div className="flex items-center gap-6 text-white/90 text-sm">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4" />
                      <span style={{ fontFamily: "'Hepta Slab', serif" }}>{expandedPost.author}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      <span style={{ fontFamily: "'Hepta Slab', serif" }}>{expandedPost.date}</span>
                    </div>
                    <span style={{ fontFamily: "'Hepta Slab', serif" }}>{expandedPost.readTime}</span>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-8 md:p-12">
                <div 
                  className="prose prose-lg max-w-none"
                  style={{ fontFamily: "'Hepta Slab', serif" }}
                  dangerouslySetInnerHTML={{ __html: expandedPost.fullContent }}
                />

                {/* Stats */}
                <div className="flex items-center gap-8 mt-12 pt-8 border-t border-gray-200">
                  <div className="flex items-center gap-2 text-gray-600">
                    <Eye className="w-5 h-5" />
                    <span style={{ fontFamily: "'Hepta Slab', serif" }}>{expandedPost.views} views</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600">
                    <MessageCircle className="w-5 h-5" />
                    <span style={{ fontFamily: "'Hepta Slab', serif" }}>{expandedPost.comments} comments</span>
                  </div>
                  <button
                    onClick={(e) => toggleLike(expandedPost.id, e)}
                    className="flex items-center gap-2 text-gray-600 hover:text-red-500 transition-colors"
                  >
                    <Heart className={`w-5 h-5 ${likedPosts[expandedPost.id] ? 'fill-red-500 text-red-500' : ''}`} />
                    <span style={{ fontFamily: "'Hepta Slab', serif" }}>{getLikeCount(expandedPost.id, expandedPost.likes)} likes</span>
                  </button>
                </div>

                {/* Back Button */}
                <div className="text-center mt-8">
                  <button
                    onClick={closePost}
                    className="px-8 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-full font-bold hover:from-orange-600 hover:to-orange-700 transition-all shadow-lg hover:shadow-xl"
                    style={{ fontFamily: "'Hepta Slab', serif" }}
                  >
                    Back to Blog
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Custom CSS for hiding scrollbar */}
      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .prose h3 {
          font-family: 'Diplomata', 'Arbutus', cursive !important;
          font-size: 1.5rem;
          font-weight: 900;
          color: #1f2937;
          margin-top: 2rem;
          margin-bottom: 1rem;
        }
        .prose p {
          font-family: 'Hepta Slab', serif !important;
          font-size: 1.125rem;
          line-height: 1.75;
          color: #4b5563;
          margin-bottom: 1.5rem;
        }
      `}</style>
    </section>
  );
};

export default Blog;